import {prepWeightFactor, retainedFactor} from 'fed-lib/src/kitchen/retained';
import moment from 'moment';

// stray whitespace in a name (a leading space, a doubled interior space) is
// invisible when rendered but makes the name a different string, so the
// name-keyed grouping on the packing screen splits the customer in two.
// keep in sync with fed-lib/src/normalize-name.js
export function normalizeName(name) {
  return typeof name === 'string' ? name.trim().replace(/\s+/g, ' ') : name;
}

export function deepCopy(o) {
  if (o) {
    return JSON.parse(JSON.stringify(o));
  } else {
    return o;
  }
}

// "19m 5s", or "1h 2m 5s" past an hour; "0s" for nothing. For people reading
// a card, unlike formatDuration's fractional minutes which suit spreadsheets.
export function formatMinutesSeconds(ms) {
  if (ms === null || ms === undefined || ms === '' || isNaN(ms)) return '';
  const sign = ms < 0 ? '-' : '';
  const totalSeconds = Math.round(Math.abs(ms) / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const parts = [];
  if (hours > 0) parts.push(`${hours}h`);
  if (hours > 0 || minutes > 0) parts.push(`${minutes}m`);
  parts.push(`${seconds}s`);
  return sign + parts.join(' ');
}

// Task CSV exports build their rows with these columns holding raw
// milliseconds; formatDurationColumns renders them the way the person chose.
export const DURATION_COLUMNS = ['totalDuration', 'duration', 'estimate', 'differenceFromEstimate'];
export const DURATION_FORMATS = [
  {text: 'Minutes & seconds (19m 5s)', value: 'minutesSeconds'},
  {text: 'Decimal minutes (19.09)', value: 'minutes'},
];
const DURATION_FORMAT_STORAGE_KEY = 'task-csv-duration-format';

export function loadDurationFormat() {
  const stored = localStorage.getItem(DURATION_FORMAT_STORAGE_KEY);
  return DURATION_FORMATS.some(f => f.value === stored) ? stored : 'minutesSeconds';
}

export function saveDurationFormat(format) {
  localStorage.setItem(DURATION_FORMAT_STORAGE_KEY, format);
}

// Each duration column becomes text in the chosen format; decimal minutes
// get a "(mins)" suffix on the column name so the spreadsheet says its unit.
export function formatDurationColumns(rows, format) {
  const decimal = format === 'minutes';
  return rows.map(row => {
    const out = {};
    for (const [key, value] of Object.entries(row)) {
      if (!DURATION_COLUMNS.includes(key)) {
        out[key] = value;
      } else if (decimal) {
        out[`${key} (mins)`] = formatDuration(value, true);
      } else {
        out[key] = formatMinutesSeconds(value);
      }
    }
    return out;
  });
}

export function formatDuration(ms, fractionalMinutes = false) {
  if (ms && !isNaN(ms)) {
    if (fractionalMinutes) {
      return ((ms / 1000) / 60).toFixed(2);
    } else {
      const m = moment.utc(ms || 0);
      if (m.hours() > 0) {
        return m.format('HH:mm:ss');
      } else {
        return m.format('mm:ss');
      }
      // const minutes = `${Math.floor((ms / 1000) / 60)}`.padStart(2,'0');
      // const seconds = `${Math.round((((ms / 1000) / 60) % 1) * 60)}`.padStart(2,'0');
      // return `${minutes}:${seconds}`;
    }
  } else {
    return '';
  }
}

export function dateFormatted(date, {append, prepend, formatString} = {}) {
  formatString =
    (prepend || '') +
    (formatString || 'dddd MMM DD') +
    (append || '');
  if (date) {
    return moment(date).format(formatString);
  } else {
    return '';
  }
}

export function getDatesInRange(from, to, limit = 31) {
  if (!from || !to) {
    return [];
  }
  from = moment(from);
  to = moment(to);

  const dates = [];
  const diff = moment.duration(to.diff(from));

  if (diff.asDays() >= 0 && diff.asDays() <= limit) {
    // console.log('adding days ', {from, to})
    while (from.isSameOrBefore(to)) {
      const dateString = getDateString(from);
      // console.log('added date', dateString);
      dates.push(dateString);
      from.add(1, 'day');
    }
  } else {
    window.alert(diff.asDays() + ' days is above the limit of ' + limit);
  }
  // console.log('getDatesInRange', dates);
  return dates;
}


function getStartOfWeek(date) {
  return getDateString(moment(date).startOf('week'));
}

export function getDateString(date) {
  if (date) {
    return moment(date).format('YYYY-MM-DD');
  } else {
    return '';
  }
}

export function formatWeightWithUnits(amount) {
  const units = ['g', 'kg'];
  const decimalPlaces = [1, 2];
  return formatWeight(amount, units, decimalPlaces);
}


export function formatWeight(value, units = [], decimalPlaces = []) {
  units = [...units];
  decimalPlaces = [...decimalPlaces];

  if (value === undefined || Number.isNaN(value)) {
    // eslint-disable-next-line no-debugger
    // debugger;
    console.log('warning formatWeight called with ' + value);
    // throw new Error('fail ' + value);
    return 0;
  }
  value = Number.parseFloat(value);
  // return value.toFixed(2);

  if (value === 0) {
    const unit = units[0] || '';
    return `0${unit}`;
  }

  let selectedUnit = units.shift() || '';
  let fractionDigits = decimalPlaces.shift();
  if (fractionDigits === undefined) {
    fractionDigits = 2;
  }
  while (units.length > 0 && value >= 999) {
    selectedUnit = units.shift();
    if (decimalPlaces.length > 0) {
      fractionDigits = decimalPlaces.shift();
    }
    value = value / 1000;
  }

  const numberPart = value.toFixed(fractionDigits)
    // remove trailing zeros
    .replace(/\.0+$/, '');

  if (numberPart === '-0') {
    return `0${selectedUnit}`;
  } else {
    return `${numberPart}${selectedUnit}`;
  }
}

function convertIngredient(i) {
  if (!i.fooddescriptionsimple) {
    i.fooddescriptionsimple = i.food_name ? i.food_name.fooddescriptionsimple : '';
  }

  // ugh, so much crap by different naming
  const portionBasis = i.portion_basis || i.measureid;
  const hasPortionBasisButNotStandardMeasureAndNoConversion = portionBasis && portionBasis != 1455 && !i.conversionfactorvalue;
  if (hasPortionBasisButNotStandardMeasureAndNoConversion) {
    console.log(`convertIngredient ${i.fooddescriptionsimple} cf=${!i.conversionfactorvalue}`, i);
    console.warn('measure is not in 100g', portionBasis);
    const mealOrComponent = i.mealid ? `M${i.mealid}` : `C${i.component_id}`;
    alert('Ingredient has non standard measure!\n\n'
      + `${i.fooddescriptionsimple} measure type ${portionBasis} in ${mealOrComponent}`);
    throw new Error('cannot convert amount');
  }
  const shoppingmultiplier = i.shoppingmultiplier || (i.food_name && i.food_name.shoppingmultiplier);
  if (shoppingmultiplier) {
    // i.percentYield = ((1 / m) * 100).toFixed();
    i.percentChange = ((shoppingmultiplier - 1) * 100).toFixed();
  }
  const portionMultiplier = i.portion_multiplier || i.portionmultiplier;  // I HATE THIS SO MUCH
  i.cookedAmount = (i.conversionfactorvalue || 1) * portionMultiplier * 100;
  i.rawAmount = i.percentChange
    ? i.cookedAmount + (i.cookedAmount * (i.percentChange / 100))
    : i.cookedAmount;

  i.buyAmount = (i.trim_multiplier || 1) * i.rawAmount;
  if (i.trim_multiplier) {
    i.percentTrim = ((i.trim_multiplier - 1) * 100).toFixed();
  }

  i.cookedAmounts = {
    medium: i.cookedAmount,
  };

  i.rawAmounts = {
    medium: i.rawAmount
  }

  i.buyAmounts = {
    medium: i.buyAmount
  }
  let c = i.component;
  if (!c) {
    console.error('no component found', {component_id: i.component_id, i});
    c = {monosized: true};
  }

  if (c.monosized) {
    i.rawAmounts.small = i.rawAmount;
    i.cookedAmounts.small = i.cookedAmount;
    i.rawAmounts.large = i.rawAmount;
    i.cookedAmounts.large = i.cookedAmount;
    i.buyAmounts.small = i.buyAmount;
    i.buyAmounts.large = i.buyAmount;
  } else {
    i.rawAmounts.small = c.small_ratio * i.rawAmount;
    i.cookedAmounts.small = c.small_ratio * i.cookedAmount;
    i.rawAmounts.large = c.large_ratio * i.rawAmount;
    i.cookedAmounts.large = c.large_ratio * i.cookedAmount;
    i.buyAmounts.small = i.buyAmount * c.small_ratio;
    i.buyAmounts.large = i.buyAmount * c.large_ratio;
  }

  if (!i.fooddescription) {
    i.fooddescription = '';
  }

  if (hasPortionBasisButNotStandardMeasureAndNoConversion) {
    console.log('convertedIngredient', i);
  }

  return i;
}

function convertIngredientBack(i) {
  i = Object.assign({}, i);
  const p = i.percentChange;
  // if value was set, or value was previously set, update it
  if (p || i.shoppingmultiplier) {
    i.shoppingmultiplier = (p / 100) + 1;
  }

  // i.cookedAmount = (i.conversionfactorvalue || 1) * i.portion_multiplier * 100;
  i.portion_multiplier = (i.cookedAmount / 100) / (i.conversionfactorvalue || 1);
  return i;
}

function getBestBefore(date) {
  // March 17, 2020
  return moment(date).add(2, 'days').format('MMM D, YYYY');
}

function getFullDay(date) {
  return moment(date).format('dddd');
}

export {
  getStartOfWeek,
  convertIngredient,
  convertIngredientBack,
  getBestBefore,
  getFullDay
}

export function ensureNumber(value) {
  return Number.parseFloat(value);
}

/**
 * Treat an empty, cleared or non-numeric form input as zero.
 */
export function blankAsZero(value) {
  if (value === undefined || value === null || value === '') {
    return 0;
  }
  const number = Number(value);
  return Number.isNaN(number) ? 0 : number;
}

export function ensureValuesNumbers(object) {
  return Object.fromEntries(
    Object.entries(object)
      .map(([k, v]) => {
        if (typeof v === 'number') {
          return [k, v];
        }
        if (typeof v === 'string') {
          return [k, Number(v)];
        }
        if (v === undefined || v === null) {
          return [k, 0];
        }
        if (typeof v === 'object') {
          return [k, ensureValuesNumbers(v)];
        }
        alert(`Could not convert ${typeof v} to a numbers`);
        console.error('failure converting to number', {k, v, object});
        throw new Error(`Could not convert ${typeof v} to a numbers`);
      })
  )
}

export function ensureInt(value) {
  return Number.parseInt(value);
}

export function assignIngredientAmounts(newIngredient, oldIngredient) {
  newIngredient.component_id = oldIngredient.component_id;
  newIngredient.component = oldIngredient.component;
  newIngredient.cookedAmount = oldIngredient.cookedAmount;
  newIngredient.percentChange = oldIngredient.percentChange;
  // we always save in 1455 - measure of 100g units, as this is defined for all ingredients
  newIngredient.portion_basis = 1455; //oldIngredient.portion_basis;
  newIngredient.portion_multiplier = oldIngredient.cookedAmount / 100;
  // console.log('assignIngredientAmounts', {newIngredient,oldIngredient});
}

export function removeCC(id) {
  try {
    const idCleaned = parseInt(`${id}`.replace(/CC$/, ''));
    if (isNaN(idCleaned)) {
      console.warn('unabled to parse id', id);
      throw new Error('not able to parse id');
    }
    return idCleaned;
  } catch (e) {
    return id;
  }
}

export function extractFoodId(item) {
  try {
    return item.name.split(':::')[0].split(' ').reverse()[0].replace('[', '').replace(']', '');
  } catch (e) {
    console.warn('failed to extract id', e);
    return '?';
  }
}

export function promisePause(timeout) {
  return new Promise(resolve => setTimeout(() => resolve(true), timeout));
}


const CSV_DELIMITER = ',';
// a double quote inside a cell would end the cell early, so write it as two apostrophes instead
const CSV_QUOTE_REPLACEMENT = "''";

function csvCell(value) {
  if (Array.isArray(value)) {
    value = value.join(', ');
  } else if (typeof value === 'object') {
    value = Object.entries(value || {}).map(([k, v]) => `${k}:${v}`).join(' ');
  }
  return `"${`${value}`.replace(/"/g, CSV_QUOTE_REPLACEMENT)}"`;
}

function csvRow(values) {
  return values.map(csvCell).join(CSV_DELIMITER);
}

// header row followed by one row per item, using the keys of the first item as columns
function csvTable(arrData) {
  return [
    csvRow(Object.keys(arrData[0])),
    ...arrData.map(item => csvRow(Object.values(item)))
  ];
}

function downloadCsv(csvContent, filename) {
  const link = document.createElement('a');
  const csvBlob = new Blob([csvContent], {type: 'text/csv'});
  link.href = URL.createObjectURL(csvBlob);
  link.setAttribute('download', filename);
  link.click();
}

export function csvExport(arrData, filename) {
  if (!arrData || arrData.length === 0) {
    alert('no data to export');
    return;
  }
  downloadCsv(csvTable(arrData)
    .join('\n')
    .replace(/(^\[)|(\]$)/gm, ''), filename);
}

/**
 * Export several tables into a single csv file. Each group becomes a section made of a title row,
 * its own column header row and its rows - sections are separated by a blank line.
 * @param groups [{title, rows}] where rows is an array of objects keyed by column name
 * @param filename name of the downloaded file
 */
export function csvExportGrouped(groups, filename) {
  const sections = groups
    .filter(({rows}) => rows && rows.length > 0)
    .map(({title, rows}) => [csvRow([title]), ...csvTable(rows)].join('\n'));

  if (sections.length === 0) {
    alert('no data to export');
    return;
  }
  downloadCsv(sections.join('\n\n'), filename);
}

export function csvAsTable(items, headers, search) {
  search = search && new RegExp(search, 'i');
  const rows = [];
  // allow gets from props like thing.subthing
  const get = (from, ...selectors) =>
    [...selectors].map(s =>
      s
        // .replace(/\[([^\[\]]*)\]/g, '.$1.')
        .split('.')
        .filter(t => t !== '')
        .reduce((prev, cur) => prev && prev[cur], from)
    );
  for (const item of items) {
    if (search) {
      if (!search.test(headers.map(h => get(item, h.value)).join(' ')))
        continue;
    }
    rows.push(Object.fromEntries(headers.map(h => {
      let itemElement = get(item, h.value);
      if (itemElement.length && itemElement.length === 1) {
        // if element is array of one thing, just make it thing
        itemElement = itemElement[0];
      }
      return [h.text, itemElement || ''];
    })))
  }
  console.log('rows', rows);
  return rows;
}

const currencyFormat = new Intl.NumberFormat('en-CA',
  {style: 'currency', currency: 'CAD'});

export function formatCurrency(value) {
  return currencyFormat.format(value / 100);
}

// export function formatCurrency(value, {trimZeroCents} = {}) {
// value = value || 0;
// const str = `$${(parseInt(value) / 100).toFixed(2)}`;
// if (trimZeroCents) {
//   return str.replace(/\.00$/, '');
// }
// return str;
// }


export function shortenBin(binString) {
  function abbr(name) {
    return name.substring(0, 3);
  }

  function shorten(b1, b2) {
    const [name1, number1] = b1.split('-');
    const [name2, number2] = b2.split('-');
    if (name1 === name2) {
      return `${(abbr(name1))}-${number1}..${number2}`.toUpperCase();
    } else {
      return `${(abbr(name1))}-${number1}..${abbr(name2)}-${number2}`.toUpperCase();
    }
  }

  const bins = (binString || '').split(',');
  if (bins.length > 1) {
    return shorten(bins[0], bins[bins.length - 1]);
  } else {
    return bins.join(', ');
  }
}

export function capitalise(str) {
  if (!str) {
    return '';
  }
  return str.charAt(0).toUpperCase() + str.slice(1);
}

// words that stay lowercase in a title unless they are the first or last word
const TITLE_CASE_MINOR_WORDS = new Set([
  'a', 'an', 'the',
  'and', 'but', 'or', 'nor', 'for', 'so', 'yet',
  'as', 'at', 'by', 'in', 'of', 'on', 'per', 'to', 'up', 'via', 'with'
]);

export function titleCase(str) {
  if (!str) {
    return '';
  }
  const words = str.split(' ');
  const last = words.length - 1;
  return words.map((word, i) => {
    if (i !== 0 && i !== last && TITLE_CASE_MINOR_WORDS.has(word.toLowerCase())) {
      return word.toLowerCase();
    }
    // uppercase the first letter, leaving the rest as typed so acronyms like BBQ survive
    const letter = word.search(/[a-zA-Z]/);
    if (letter === -1) {
      return word;
    }
    return word.slice(0, letter) + word.charAt(letter).toUpperCase() + word.slice(letter + 1);
  }).join(' ');
}


/**
 * gets the production day groupings
 */
function getDayGroups(date) {
  if (moment(date).isSameOrAfter('2023-12-03', 'day')) {
    return [
      [1, 2, 3], [4, 5, 6]
    ];
  } else {
    return [
      [1, 2], [3, 4], [5, 6]
    ];
  }
}

export function getNextProductionDays(date) {
  let day = moment(date).day();
  if (day === 0) {
    //shift sunday to monday
    day = 1;
  }
  const dayGroups = getDayGroups(date);
  const group = dayGroups.find(group => !group.includes(day));
  if (group) {
    if (group[1] < 3) {
      date = moment(date).add(1, 'week');
    }
    return group.map(d => moment(date).day(d).format(moment.HTML5_FMT.DATE));
  } else {
    return [moment(date).format(moment.HTML5_FMT.DATE)];
  }
}

export function getProductionDays(date) {
  const day = moment(date).day();
  const dayGroups = getDayGroups(date);
  const group = dayGroups.find(group => group.includes(day));
  if (group) {
    return group.map(d => moment(date).day(d).format(moment.HTML5_FMT.DATE));
  } else {
    return [moment(date).format(moment.HTML5_FMT.DATE)];
  }
}

export function getDeliveryInstruction(customer, date) {
  const address = getAddress(customer, date);
  const instruction = address.delivery_instruction || customer.delivery_instruction || '';
  if (address.buzzer) {
    return `Buzzer ${address.buzzer}.  ${instruction}`;
  } else {
    return instruction;
  }
}

export function getAddress(customer, date) {
  date = moment(date);
  const day = date.format('ddd').toLowerCase();
  const is2DayDelivery = date.isSameOrAfter('2023-12-03', 'day');
  let addressMap;
  if (customer && customer.useAddress) {
    addressMap = {};
    if (is2DayDelivery) {
      if (customer.useAddress.mon_tue_wed) {
        addressMap.mon = customer.useAddress.mon_tue_wed;
        addressMap.tue = customer.useAddress.mon_tue_wed;
        addressMap.wed = customer.useAddress.mon_tue_wed;
      }
      if (customer.useAddress.thu_fri_sat) {
        addressMap.thu = customer.useAddress.thu_fri_sat;
        addressMap.fri = customer.useAddress.thu_fri_sat;
        addressMap.sat = customer.useAddress.thu_fri_sat;
      }
    } else {
      if (customer.useAddress.mon_tue) {
        addressMap.mon = customer.useAddress.mon_tue;
        addressMap.tue = customer.useAddress.mon_tue;
      }
      if (customer.useAddress.wed_thu) {
        addressMap.wed = customer.useAddress.wed_thu;
        addressMap.thu = customer.useAddress.wed_thu;
      }
      if (customer.useAddress.fri_sat) {
        addressMap.fri = customer.useAddress.fri_sat;
        addressMap.sat = customer.useAddress.fri_sat;
      }
    }
  }
  if (addressMap && addressMap[day]) {
    return addressMap[day];
  } else {
    return customer ? customer.address : {};
  }
}

export function getEarliestDefaultDeliveryTime(address, ship_date = undefined) {
  const earliestDefault = {
    mon: {
      office: '08:00'
    },
    tue: {
      office: '11:00'
    },
    thu: {
      office: '11:00'
    },
    office: '09:00',
    home: '17:00',
  }

  const location = address.location;
  const shipDay = ship_date && moment(ship_date).format('ddd').toLowerCase();
  if (earliestDefault[shipDay] && earliestDefault[shipDay][location]) {
    return earliestDefault[shipDay][location];
  } else {
    return earliestDefault[location];
  }
}


export function getLatestDefaultDeliveryTime(address, ship_date = undefined) {
  const latestDefault = {
    mon: {
      office: '12:00'
    },
    tue: {
      office: '17:00'
    },
    thu: {
      office: '17:00'
    },
    home: '22:00',
    office: '17:00'
  }

  const location = address.location;
  const shipDay = ship_date && moment(ship_date).format('ddd').toLowerCase();
  if (latestDefault[shipDay] && latestDefault[shipDay][location]) {
    return latestDefault[shipDay][location];
  } else {
    return latestDefault[location];
  }
}

export function formatDeliveryTimes(a) {
  const {earliest_time = getEarliestDefaultDeliveryTime(a), latest_time = getLatestDefaultDeliveryTime(a)} = a;
  return `${earliest_time} to ${latest_time}`;
}

export function formatAddress(a, {short = true, excludeUnit = false} = {}) {
  function formatPostcode(s) {
    return (s || '').replaceAll(' ', '').toUpperCase();
  }

  const city = short
    ? (a.city || '').replace(/\(.+\)/g, '').trim()  // regex to turn "Squamish (HOURS REMOVE THIS)" into "Squamish"
    : false;
  const unit = !excludeUnit && a.unit && a.unit.trim();
  return `${
    [
      (unit ? `${unit}-` : '') + a.line1,
      a.line2,
      city,
      formatPostcode(a.postal_code),
      // (a.location === 'office' ? 'office' : false)
    ]
      .filter(a => !!a)
      .map(s => s.trim())
      .join(', ')
  }`;
}

export function parseTimeToMinutes(time) {
  const match = /^(\d{1,2}):(\d{2})$/.exec((time || '').trim());
  if (!match) {
    return undefined;
  }
  const [, hours, minutes] = match;
  return Number(hours) * 60 + Number(minutes);
}

// the group can only be delivered once every order's window has opened
export function getEarliestDeliveryTime(orders, defaultIfNone = true) {
  const times = orders
    .map(o => {
      const address = o.address || {};
      return address.earliest_time
        || (defaultIfNone ? getEarliestDefaultDeliveryTime(address, o.ship_date) : undefined);
    })
    .filter(t => !!t)
    .sort((a, b) => parseTimeToMinutes(b) - parseTimeToMinutes(a));
  return times[0];
}

// the group must be delivered before the first order's window closes
export function getLatestDeliveryTime(orders, defaultIfNone = true) {
  const times = orders
    .map(o => {
      const address = o.address || {};
      return address.latest_time
        || (defaultIfNone ? getLatestDefaultDeliveryTime(address, o.ship_date) : undefined);
    })
    .filter(t => !!t)
    .sort((a, b) => parseTimeToMinutes(a) - parseTimeToMinutes(b));
  return times[0];
}

// a home and an office at the same street address are two different doors, so
// they must never collapse into a single delivery
export function addressGroupKey(address) {
  const isOffice = address && address.location === 'office';
  return formatAddress(address) + (isOffice ? ' (office)' : '');
}

// one delivery is one stop, so every order in it has to be deliverable at the
// same moment.  split an address into as few stops as possible, each holding
// orders whose delivery windows all overlap.
export function splitByDeliveryWindow(orders) {
  const windows = orders
    .map((order, position) => ({
      order,
      position,
      from: parseTimeToMinutes(getEarliestDeliveryTime([order])) ?? -Infinity,
      to: parseTimeToMinutes(getLatestDeliveryTime([order])) ?? Infinity,
    }))
    .sort((a, b) => (a.from - b.from) || (a.to - b.to));

  const stops = [];
  let stop;
  windows.forEach(entry => {
    // a stop can take the order only while all of its windows still share a
    // moment, so the stop's own window narrows to the overlap each time
    const overlaps = stop && Math.max(stop.from, entry.from) <= Math.min(stop.to, entry.to);
    if (overlaps) {
      stop.from = Math.max(stop.from, entry.from);
      stop.to = Math.min(stop.to, entry.to);
      stop.entries.push(entry);
    } else {
      stop = {from: entry.from, to: entry.to, entries: [entry]};
      stops.push(stop);
    }
  });

  // restore the caller's order within each stop - plenty of code reads orders[0]
  return stops.map(s => s.entries
    .sort((a, b) => a.position - b.position)
    .map(entry => entry.order));
}

// groups are keyed case-insensitively but displayed in their original case, so
// callers hand over the groups plus the original-case key for each of them
export function sortOrderGroups(groups, keyMap) {
  const sortedKeys = Object
    .keys(groups)
    .sort((a, b) => {
      const [orderA] = groups[a];
      const [orderB] = groups[b];
      if (orderA && orderA.eta && orderB && orderB.eta) {
        return orderA.eta - orderB.eta
      } else {
        return keyMap[a].localeCompare(keyMap[b]);
      }
    });
  const sortedGroups = {};
  sortedKeys.forEach(key => sortedGroups[keyMap[key]] = groups[key]);
  return sortedGroups;
}

// one group is one stop for the driver and one task in onfleet, so orders only
// share a group when they share a door (address plus home/office) and their
// delivery windows overlap
export function groupOrdersByDelivery(orders) {
  const ordersByDoor = {};
  const doorLabels = {};
  orders.forEach(o => {
    const label = addressGroupKey(o.address);
    if (label) {
      const doorKey = label.toUpperCase();
      doorLabels[doorKey] = doorLabels[doorKey] || label;
      ordersByDoor[doorKey] = ordersByDoor[doorKey] || [];
      ordersByDoor[doorKey].push(o);
    }
  });

  const groups = {};
  const keyMap = {};
  Object.entries(ordersByDoor).forEach(([doorKey, doorOrders]) => {
    const stops = splitByDeliveryWindow(doorOrders);
    stops.forEach(stopOrders => {
      // only name the window when a door has been split by it, so the usual
      // single-stop address keeps reading as a plain address
      const deliveryWindow = stops.length > 1
        ? ` (${getEarliestDeliveryTime(stopOrders)}-${getLatestDeliveryTime(stopOrders)})`
        : '';
      const label = doorLabels[doorKey] + deliveryWindow;
      keyMap[label.toUpperCase()] = label;
      groups[label.toUpperCase()] = stopOrders;
    });
  });
  return sortOrderGroups(groups, keyMap);
}

export const filterSelectedIngredients = (selectedFoodIds, allergyDetected) => {
  if (allergyDetected) {
    allergyDetected = JSON.parse(JSON.stringify(allergyDetected));
    Object.keys(allergyDetected)
      .forEach(allergy => {
        const foodIds = Object.keys(allergyDetected[allergy]);
        foodIds.forEach(foodId => {
          if (!selectedFoodIds.includes(parseInt(foodId))) {
            delete allergyDetected[allergy][foodId];
          }
        });
        if (Object.keys(allergyDetected[allergy]).length === 0) {
          delete allergyDetected[allergy];
        }
      });
  }
  return allergyDetected;
}

export function calculateSummary(orders) {
  return orders
    .reduce((s, order) => {
      s[order.meal_size] = s[order.meal_size] + order.quantity;
      s.total = s.total + order.quantity;
      return s;
    }, {
      small: 0,
      medium: 0,
      large: 0,
      total: 0
    });
}

export function filterByAllergyDetected(orders, selectedFoodIds, isDetected) {
  // console.log('filterByAllergyDetected', orders, selectedFoodIds);
  isDetected = !!isDetected;
  return orders.filter(o => {
    if (o.allergyDetected) {
      if (!selectedFoodIds || selectedFoodIds.length === 0) {
        return isDetected;
      }
      const allergyDetected = {...o.allergyDetected};
      // TODO: damn these non-allergy
      delete allergyDetected['extra protein'];
      delete allergyDetected['extra large'];
      if (Object.values(allergyDetected)
        .flatMap(a => Object.keys(a))
        .some(foodid => selectedFoodIds.includes(foodid))) {
        return isDetected;
      }
    }
    return !isDetected;
  })
}

export const normaliseIngredient =
  (meal) =>
    (i) => {
      // FIXME: this makes me mad/sad.  consistent naming failure
      return {
        mealid: meal ? meal.id : null,
        component_id: i.component_id,
        foodid: i.food_id || i.foodid,
        portion_basis: i.measure_id || i.measureid,
        portion_multiplier: i.portionmultiplier || (i.amount / 100),
        fooddescriptionsimple: i.food_name && i.food_name.fooddescriptionsimple,
        shoppingmultiplier: i.shoppingmultiplier || (i.food_name && i.food_name.shoppingmultiplier),
        price: i.food_name && i.food_name.price
      };
    }

export function formatDate(date) {
  // console.log('foramt date',[ date, moment(date).utc().format(moment.HTML5_FMT.DATE)]);
  return moment(date).utc().format(moment.HTML5_FMT.DATE);
}


export function getOrderDeadline() {

  const now = moment();
  let deadline;
  try {
    deadline = moment('Wednesday 12:00', 'dddd hh:mm');
  } catch (e) {
    console.error('failed to set config order deadline, using default midnight');
    deadline = now.clone().day(5).startOf('day')
  }

  if (now > deadline) {
    return deadline.add(1, 'week').format(moment.HTML5_FMT.DATETIME_LOCAL);
  } else {
    return deadline.format(moment.HTML5_FMT.DATETIME_LOCAL);
  }
}


export function getNextOrderingDate() {
  const deadline = moment(getOrderDeadline());
  return deadline.startOf('week').add(1, 'week').format(moment.HTML5_FMT.DATE);
}


const specialRegexChars = /[/\-\\^$*+?.()|[\]{}]/g;

export function escapeRegex(string) {
  return string.replace(specialRegexChars, '\\$&');
}

export function getPlatingTimeEstimate(mealCount) {
  /**
   * logic
   2 min setup time
   1-20 meals = 1 minute/meal
   21-60 meals = .5 minutes/meal
   61+ = .25 minutes/meal
   */
  let rate;
  switch (true) {
    case (mealCount <= 20) :
      rate = 1;
      break;
    case (mealCount <= 60) :
      rate = 0.5;
      break;
    default: // over 60
      rate = .25;
  }
  const minutes = 2 + (mealCount * rate);
  // console.log('plating estimate', {mealCount, rate, minutes});
  return minutes * 60 * 1000;
}

export function getComponentSizeRatio(component, size) {
  if (size === 'medium' || component.monosized) {
    return 1;
  } else {
    const defaultRatio = {
      small: 0.75,
      large: 1.25
    }
    return component[`${size}_ratio`] || defaultRatio[size];
  }
}

export function getRawComponentAmountForMeal(component, meal, size, dates) {
  // from FULL_PREP_WEIGHT_START_DATE prep amounts use the full weight;
  // before it, ignore_prep_weight ingredients are excluded (old logic)
  const ingredientsInComponent = meal.meal_food
    .filter(mf => mf.component_id === component.id);
  const amounts = ingredientsInComponent
    .map(mf => mf.rawAmount * prepWeightFactor(mf, dates));
  // console.log('amounts', amounts);
  const componentAmount = amounts
    .reduce((i, sum) => i + sum, 0);
  const ratio = getComponentSizeRatio(component, size);
  // console.log('component amount', component.name, componentAmount, ratio)
  return componentAmount * ratio;
}

export function getComponentAmountForMeal(component, meal, size) {
  // cooked weight of one portion of the component: each ingredient counts
  // only the fraction retained in the final dish, the same portion weight the
  // stickers and plating use (an ignore_prep_weight stock counts 5%, not 100%)
  const ingredientsInComponent = meal.meal_food
    .filter(mf => mf.component_id === component.id);
  const amounts = ingredientsInComponent
    .map(mf => mf.cookedAmount * retainedFactor(mf));
  // console.log('amounts', amounts);
  const componentAmount = amounts
    .reduce((i, sum) => i + sum, 0);
  const ratio = getComponentSizeRatio(component, size);
  // console.log('component amount', component.name, componentAmount, ratio)
  return componentAmount * ratio;
}

export function sortBySize(countMap) {
  const result = {};
  for (const size of new Set(['small', 'medium', 'large', ...Object.keys(countMap)])) {
    if (countMap[size])
      result[size] = countMap[size];
  }
  return result;
}


export function extractDateFromDateProduction(dateProduction) {
  let date = (dateProduction || '').split('_')[0];
  return date && date !== 'undefined' ? date : undefined;
}

export function extractProductionFromDateProduction(dateProduction) {
  let production = (dateProduction || '').split('_')[1];
  return production && production !== 'undefined' ? production : undefined;
}

export const todMapToString = {
  1: 'breakfast'.toUpperCase(),
  2: 'lunch'.toUpperCase(),
  3: 'dinner'.toUpperCase(),
  4: 'extras'.toUpperCase()
}


export function valueSearch(value, search, item) {
  try {
    return Object.values(item).some(v => v && v.toString().toLowerCase().includes(search));
  } catch (e) {
    console.warn('filter failed', e);
  }
}


export function getBrowserId() {
  const animals = [
    "Aardvark",
    "Albatross",
    "Alligator",
    "Alpaca",
    "Ant",
    "Anteater",
    "Antelope",
    "Ape",
    "Armadillo",
    "Donkey",
    "Baboon",
    "Badger",
    "Barracuda",
    "Bat",
    "Bear",
    "Beaver",
    "Bee",
    "Bison",
    "Boar",
    "Buffalo",
    "Butterfly",
    "Camel",
    "Capybara",
    "Caribou",
    "Cassowary",
    "Cat",
    "Caterpillar",
    "Cattle",
    "Chamois",
    "Cheetah",
    "Chicken",
    "Chimpanzee",
    "Chinchilla",
    "Chough",
    "Clam",
    "Cobra",
    "Cockroach",
    "Cod",
    "Cormorant",
    "Coyote",
    "Crab",
    "Crane",
    "Crocodile",
    "Crow",
    "Curlew",
    "Deer",
    "Dinosaur",
    "Dog",
    "Dogfish",
    "Dolphin",
    "Dotterel",
    "Dove",
    "Dragonfly",
    "Duck",
    "Dugong",
    "Dunlin",
    "Eagle",
    "Echidna",
    "Eel",
    "Eland",
    "Elephant",
    "Elk",
    "Emu",
    "Falcon",
    "Ferret",
    "Finch",
    "Fish",
    "Flamingo",
    "Fly",
    "Fox",
    "Frog",
    "Gaur",
    "Gazelle",
    "Gerbil",
    "Giraffe",
    "Gnat",
    "Gnu",
    "Goat",
    "Goldfinch",
    "Goldfish",
    "Goose",
    "Gorilla",
    "Goshawk",
    "Grasshopper",
    "Grouse",
    "Guanaco",
    "Gull",
    "Hamster",
    "Hare",
    "Hawk",
    "Hedgehog",
    "Heron",
    "Herring",
    "Hippopotamus",
    "Hornet",
    "Horse",
    "Human",
    "Hummingbird",
    "Hyena",
    "Ibex",
    "Ibis",
    "Jackal",
    "Jaguar",
    "Jay",
    "Jellyfish",
    "Kangaroo",
    "Kingfisher",
    "Koala",
    "Kookabura",
    "Kouprey",
    "Kudu",
    "Lapwing",
    "Lark",
    "Lemur",
    "Leopard",
    "Lion",
    "Llama",
    "Lobster",
    "Locust",
    "Loris",
    "Louse",
    "Lyrebird",
    "Magpie",
    "Mallard",
    "Manatee",
    "Mandrill",
    "Mantis",
    "Marten",
    "Meerkat",
    "Mink",
    "Mole",
    "Mongoose",
    "Monkey",
    "Moose",
    "Mosquito",
    "Mouse",
    "Mule",
    "Narwhal",
    "Newt",
    "Nightingale",
    "Octopus",
    "Okapi",
    "Opossum",
    "Oryx",
    "Ostrich",
    "Otter",
    "Owl",
    "Oyster",
    "Panther",
    "Parrot",
    "Partridge",
    "Peafowl",
    "Pelican",
    "Penguin",
    "Pheasant",
    "Pig",
    "Pigeon",
    "Pony",
    "Porcupine",
    "Porpoise",
    "Quail",
    "Quelea",
    "Quetzal",
    "Rabbit",
    "Raccoon",
    "Rail",
    "Ram",
    "Rat",
    "Raven",
    "Red deer",
    "Red panda",
    "Reindeer",
    "Rhinoceros",
    "Rook",
    "Salamander",
    "Salmon",
    "Sand Dollar",
    "Sandpiper",
    "Sardine",
    "Scorpion",
    "Seahorse",
    "Seal",
    "Shark",
    "Sheep",
    "Shrew",
    "Skunk",
    "Snail",
    "Snake",
    "Sparrow",
    "Spider",
    "Spoonbill",
    "Squid",
    "Squirrel",
    "Starling",
    "Stingray",
    "Stinkbug",
    "Stork",
    "Swallow",
    "Swan",
    "Tapir",
    "Tarsier",
    "Termite",
    "Tiger",
    "Toad",
    "Trout",
    "Turkey",
    "Turtle",
    "Viper",
    "Vulture",
    "Wallaby",
    "Walrus",
    "Wasp",
    "Weasel",
    "Whale",
    "Wildcat",
    "Wolf",
    "Wolverine",
    "Wombat",
    "Woodcock",
    "Woodpecker",
    "Worm",
    "Wren",
    "Yak",
    "Zebra"
  ];
  let fedBrowserId = '';
  try {
    const storageKey = 'k.fed-browser-id';
    fedBrowserId = window.localStorage.getItem(storageKey);
    if (!fedBrowserId) {
      const randomAnimal = animals[Math.floor(Math.random() * animals.length)];
      fedBrowserId = `${randomAnimal}-${(crypto && crypto.randomUUID && crypto.randomUUID()) || ("no-crypto-id" + Math.random().toString(16).slice(2))}`;
      window.localStorage.setItem(storageKey, fedBrowserId);
    }
    console.log('fedBrowserId', fedBrowserId)
  } catch (e) {
    console.warn('failed to generate unique browser id', e);
  }
  return fedBrowserId;
}

export function formatOrderType(order_type) {
  const map = {
    full: 'Stream Meal',
    chefs_choice: 'Chef\'s Choice',
    shipping: 'Shipping',
  }
  return map[order_type] || order_type;
}

/**
 * The photo to show the plating team - the packaged meal if we have one,
 * otherwise whatever else was shot for it.
 */
export function getMealImageUrl(meal) {
  const images = (meal && meal.sku_images) || {};
  const image = images.packaged || images.plated || images.closeup;
  return image && image.url;
}

/**
 * The same photo rotated and cropped to the plate, as the plating screens show it.
 */
export function getPlatingImageUrl(meal) {
  const url = getMealImageUrl(meal);
  return url && `${url.replace('/small', '')}/rotate=90,width=500,trim=350;600;350;600`;
}
